import team from '../api/team.api'
import store from './index.js'
// root state object.
// each Vuex instance is just a single state tree.
const state = {
  teams: [],
  publishedTeams: [],
  rosters: [],
  detailedTeam: null,
  fullDetailedTeam: null,
  detailedRoster: null,
  teamInfosForReport: null
}

// mutations are operations that actually mutates the state.
// each mutation handler gets the entire state tree as the
// first argument, followed by additional payload arguments.
// mutations must be synchronous and can be recorded by plugins
// for debugging purposes.
const mutations = {
  rosterList (state, allRosters) {
    state.rosters = allRosters
  },
  rosterDetails (state, detailedRoster) {
    state.detailedRoster = detailedRoster
  },
  refresh (state, allTeams) {
    state.teams = allTeams
  },
  refreshPublishedTeams (state, publishedTeams) {
    state.publishedTeams = publishedTeams
  },
  updateDetailedTeam (state, team) {
    state.detailedTeam = team
  },
  updateFullDetailedTeam (state, team) {
    state.fullDetailedTeam = team
  },
  removeDetailedTeam (state) {
    state.detailedTeam = null
  },
  purgeDetailedRoster (state) {
    state.detailedRoster = null
  },
  updateTeamInfosForReport (state, value) {
    state.teamInfosForReport = value
  }
}

// actions are functions that causes side effects and can involve
// asynchronous operations.
const actions = {
  getRosterList (context) {
    team.api.getRosterList()
      .then(response => {
        context.commit('rosterList', response.data)
      },
      error => {
        console.log('impossible to load roster list from api: ')
        console.log(error.text())
      })
  },
  getDetailedRoster (context, id) {
    team.api.getDetailedRoster(id)
      .then(response => {
        context.commit('rosterDetails', response.data)
      },
      error => {
        console.log('impossible to load roster details from api: ')
        console.log(error.text())
      })
  },
  refreshTeamList (context) {
    team.api.getAllteams()
      .then(response => {
        context.commit('refresh', response.data)
      },
      error => {
        console.log('impossible to load team list from api: ')
        console.log(error.text())
      })
  },
  refreshTeamListForLeague (context, leagueId) {
    team.api.getAllteamsForLeague(leagueId)
      .then(response => {
        context.commit('refresh', response.data)
      },
      error => {
        console.log('impossible to load team list from api: ')
        console.log(error.text())
      })
  },
  refreshTeamListForClub (context, clubId) {
    team.api.getAllteamsForClub(clubId)
      .then(response => {
        context.commit('refresh', response.data)
      },
      error => {
        console.log('impossible to load team list from api: ')
        console.log(error.text())
      })
  },
  getOwnPublishedTeams (context) {
    team.api.getOwnPublishedTeams()
      .then(response => {
        context.commit('refreshPublishedTeams', response.data)
      },
      error => {
        console.log('impossible to load team list from api: ')
        console.log(error.text())
      })
  },
  refreshPublishedTeamList (context) {
    team.api.getPublishedTeams()
      .then(response => {
        context.commit('refreshPublishedTeams', response.data)
      },
      error => {
        console.log('impossible to load team list from api: ')
        console.log(error.text())
      })
  },
  getDetailedTeam (context, id) {
    // if (state.detailedTeam && state.detailedTeam.id === parseInt(id)) {
    //  return
    // }
    team.api.getDetailedTeam(id)
      .then(response => {
        context.commit('updateDetailedTeam', response.data)
      },
      error => {
        console.log('impossible to load detailed team : ' + id)
        console.log(error)
      })
  },
  getFullDetailedTeam (context, id) {
    team.api.getFullDetailedTeam(id)
      .then(response => {
        context.commit('updateFullDetailedTeam', response.data)
      },
      error => {
        console.log('impossible to load fully detailed team : ' + id)
        console.log(error)
      })
  },
  invalidateTeamList (context) {
    context.commit('refresh', null)
  },
  invalidateDetailedTeam (context) {
    context.commit('removeDetailedTeam')
  },
  purgeDetailedTeam (context) {
    context.commit('updateDetailedTeam', null)
  },
  invalidatedDetailedRoster (context) {
    context.commit('purgeDetailedRoster')
  },
  reloadDetailedTeam (context) {
    team.api.getDetailedTeam(context.state.detailedTeam.id)
      .then(response => {
        context.commit('updateDetailedTeam', response.data)
      },
      error => {
        console.log('impossible to load detailed team : ' + context.state.detailedTeam.id)
        console.log(error)
      })
  },
  clearTeamInfosForMatchReport (context) {
    context.commit('updateTeamInfosForReport', null)
  },
  getTeamInfosForMatchReport (context, id) {
    team.api.getTeamsInfosForMatchReport(id)
      .then(response => {
        context.commit('updateTeamInfosForReport', response.data)
      },
      error => {
        console.log('impossible to load team infos for report: ' + id)
        console.log(error)
      })
  }
}

// getters are functions
const getters = {
  teamList: state => {
    return state.teams
  },
  filteredTeamList: state => {
    if (store.getters.activatedLeague && state.teams) {
      let al = store.getters.activatedLeague
      let filtered = state.teams.filter(team => { return team.league !== undefined && team.league.id === al })
      return filtered
    }
    return state.teams
  },
  teamListForLeague: (state, getters) => (leagueId) => {
    let filtered = state.teams.filter(team => { return team.league !== undefined && team.league.id === leagueId })
    return filtered
  },
  publishedTeamList: state => {
    return state.publishedTeams
  },
  rosterList: state => {
    return state.rosters
  },
  rosterListForClub: (state, getters) => (clubId) => {
    let filtered = state.rosters.filter(roster => {
      return roster.club_list !== undefined && roster.club_list.find(club => {
        return club === clubId
      })
    })
    return filtered
  },
  detailedTeam: state => {
    return state.detailedTeam
  },
  fullDetailedTeam: state => {
    return state.fullDetailedTeam
  },
  detailedRoster: state => {
    return state.detailedRoster
  },
  teamListForLeagueAndRankingGroup: (state, getters) => (leagueId, rgId) => {
    let filtered = getters.teamListForLeague(leagueId)
    return filtered.filter(team => { return team.ranking_group === rgId })
  },
  franchiseList: (state) => {
    if (state.teams) {
      return state.teams.filter(team => {
        return team.status === 5
      })
    }
    return []
  },
  teamsForReport: (state) => {
    return state.teamInfosForReport
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}




// WEBPACK FOOTER //
// ./src/store/team.js